import type { LoxoKpiRow } from './api'
import type { LoxoScoringConfig } from './api'
import type { TargetResolver } from './dynamicTargets'

export function calculatePercent(
  actual: number,
  target: number | null,
  isPercentMetric: boolean
): number | null {
  if (isPercentMetric) return actual
  if (target == null || target === 0) return null
  return actual / target
}

export function calculateScore(
  percent: number | null,
  percentValues: number[],
  scoreValues: number[]
): number | null {
  if (percent == null) return null
  if (percentValues.length === 0 || scoreValues.length === 0) return null

  for (let i = 0; i < percentValues.length; i++) {
    if (percent < percentValues[i]) {
      return scoreValues[i] ?? null
    }
  }
  return scoreValues[scoreValues.length - 1] ?? null
}

export type ScoringOverrides = Record<
  string,
  {
    month_target?: number | null
    month_percent_values?: number[]
    month_score_values?: number[]
  }
>

export type KpiDetail = {
  key: string
  name: string
  actual: number
  target: number | null
  percent: number | null
  score: number | null
  configured: boolean
}

export type UserMonthResult = {
  user_id: number
  name: string
  monthPoints: Record<string, number>
  totalPoints: number
  kpiDetails: Record<string, KpiDetail[]>
}

export function simulateScoring(
  loxoData: LoxoKpiRow[],
  configs: LoxoScoringConfig[],
  overrides: ScoringOverrides,
  months: string[],
  disabledKeys?: Set<string>,
  targetResolver?: TargetResolver | null
): UserMonthResult[] {
  const configByKey = new Map<string, LoxoScoringConfig>()
  configs.forEach((c) => configByKey.set(c.key, c))

  // Group data by user_id
  const userMap = new Map<number, { name: string; data: LoxoKpiRow[] }>()
  for (const row of loxoData) {
    let entry = userMap.get(row.user_id)
    if (!entry) {
      entry = { name: row.name, data: [] }
      userMap.set(row.user_id, entry)
    }
    entry.data.push(row)
  }

  const results: UserMonthResult[] = []

  userMap.forEach(({ name, data }, userId) => {
    const monthPoints: Record<string, number> = {}
    const kpiDetails: Record<string, KpiDetail[]> = {}
    let totalPoints = 0

    for (const month of months) {
      const monthData = data.filter((r) => r.period.startsWith(month))

      // Build per-KPI actuals for this month
      const actualsByKpi = new Map<string, number>()
      for (const row of monthData) {
        actualsByKpi.set(row.kpi, (actualsByKpi.get(row.kpi) ?? 0) + row.value)
      }

      // Also include KPIs that have config but no data (zero actual)
      const allKpiKeysSet = new Set<string>()
      actualsByKpi.forEach((_, k) => allKpiKeysSet.add(k))
      configs.forEach((c) => allKpiKeysSet.add(c.key))
      const allKpiKeys = Array.from(allKpiKeysSet)

      let monthScore = 0
      const details: KpiDetail[] = []

      for (const kpiKey of allKpiKeys) {
        if (disabledKeys?.has(kpiKey)) continue

        const config = configByKey.get(kpiKey)
        const actual = actualsByKpi.get(kpiKey) ?? 0
        const override = overrides[kpiKey]

        if (!config) {
          details.push({
            key: kpiKey,
            name: kpiKey,
            actual,
            target: null,
            percent: null,
            score: null,
            configured: false,
          })
          continue
        }

        const target = targetResolver
          ? targetResolver(userId, kpiKey, month)
          : override?.month_target !== undefined
            ? override.month_target
            : config.month_target
        const percentValues =
          override?.month_percent_values ?? config.month_percent_values
        const scoreValues =
          override?.month_score_values ?? config.month_score_values

        const percent = calculatePercent(
          actual,
          target,
          config.is_percent_metric
        )
        const score = calculateScore(percent, percentValues, scoreValues)

        details.push({
          key: kpiKey,
          name: config.name,
          actual,
          target,
          percent,
          score,
          configured: true,
        })

        if (score != null) {
          monthScore += score
        }
      }

      monthPoints[month] = monthScore
      totalPoints += monthScore
      kpiDetails[month] = details.sort((a, b) => a.name.localeCompare(b.name))
    }

    results.push({
      user_id: userId,
      name,
      monthPoints,
      totalPoints,
      kpiDetails,
    })
  })

  // Sort by total points descending
  results.sort((a, b) => b.totalPoints - a.totalPoints)

  return results
}
